import { useDispatch } from 'react-redux';
import {
  createColor, createColorWithOpacity,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { BOUNDARY_NO_LOCATIONS_ID } from '~/boundary/settings/boundarySettings.helpers';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { BoundaryTerritoryComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryTerritory.component';
import { isBoundaryTerritoryEmpty } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { useBoundaryBatchEditLocations } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryBatchEditLocations';
import { useBoundaryExportData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryExportData';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import { useRemoveBoundaryTerritory } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useRemoveBoundaryTerritory';
import { useBoundaryTerritoryAssignments } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useIsConnectedLayeredMapSelector } from '~/store/mapInfo/mapInfo.selectors';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { useBoundaryFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '~/store/selectors/useMapInfoSelectors';

type BoundaryTerritoryContainerProps = Readonly<{
  legendMode?: boolean;
  boundaryTerritory: BoundaryTerritory;
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
}>;

export const BoundaryTerritoryContainer = ({
  legendMode,
  boundaryTerritory,
  boundaryTerritoryGroup,
}: BoundaryTerritoryContainerProps) => {
  const dispatch = useDispatch();
  const { openModal: openExportContainedBoundaries } = useModal(ModalType.ExportContainedBoundaryData);
  const boundaryFilters = useBoundaryFilters();
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const isMapPresentational = useIsMapPresentationalSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { getBoundaryTerritoryDisplayName } = useGetBoundaryTerritoryDisplayNames();
  const { removeTerritory } = useRemoveBoundaryTerritory();
  const { exportBoundaryTerritoryData } = useBoundaryExportData();
  const { batchEditLocationsInBoundaryTerritory } = useBoundaryBatchEditLocations();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();

  const filter = boundaryFilters.get(boundaryTerritoryGroup.boundaryGroupId);
  const territoryIsCustom = boundaryTerritory.custom;
  const canDelete = territoryIsCustom && !isEditModeDisabled;
  const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
    boundaryTerritoryGroup.boundaryTerritoryGroupId,
    boundaryTerritory);
  const isNoLocations = boundaryTerritory.boundaryTerritoryId === BOUNDARY_NO_LOCATIONS_ID;
  const canExportData = !isEmpty && !isNoLocations && isBoundaryExportLocationsAllowed;

  const setBoundaryTerritoriesFilter = (filteredBoundaryTerritories: string[], showAll: TriStateRange) => {
    dispatch(setBoundaryFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories,
      showAll,
    }));
  };

  const setting = {
    isVisible: filter.filteredBoundaryTerritories.includes(boundaryTerritory.boundaryTerritoryId),
    label: getBoundaryTerritoryDisplayName(boundaryTerritory.boundaryTerritoryId,
      boundaryTerritoryGroup.boundaryTerritoryGroupId),
    selectedColor: createColorWithOpacity(boundaryTerritory.style.color, boundaryTerritory.style.opacity / 100),
    lineColor: createColor(boundaryTerritoryGroup.settings.style.lineColor),
    lineWidth: boundaryTerritoryGroup.settings.style.lineWidth,

    onToggle: () => {
      const newFilter = filter.filteredBoundaryTerritories
        .filter(territoryId => territoryId !== boundaryTerritory.boundaryTerritoryId);

      if (newFilter.length === filter.filteredBoundaryTerritories.length) {
        newFilter.push(boundaryTerritory.boundaryTerritoryId);
      }

      const showAll = newFilter.length ? TriStateRange.Partial : TriStateRange.None;

      setBoundaryTerritoriesFilter(newFilter, showAll);
    },
    isCustom: boundaryTerritory.custom,
    isEmpty,
    onExportData: canExportData ?
      () => exportBoundaryTerritoryData(boundaryTerritoryGroup.boundaryTerritoryGroupId,
        boundaryTerritory.boundaryTerritoryId) :
      undefined,
    onDelete: canDelete ? () => {
      removeTerritory(boundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryTerritory.boundaryTerritoryId);
    } : undefined,
    onBatchEditLocationsClick: !isEditModeDisabled && !isLayeredMapConnected && !isNoLocations
      ?
      () => batchEditLocationsInBoundaryTerritory(boundaryTerritoryGroup.boundaryTerritoryGroupId,
        boundaryTerritory.boundaryTerritoryId)
      :
      undefined,
    onExportContainedBoundaries: isMapPresentational ? undefined : () => openExportContainedBoundaries({
      filter: {
        type: 'boundary_territory',
        boundaryTerritoryIds: [boundaryTerritory.boundaryTerritoryId],
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
      },
    }),
  };

  return (
    <BoundaryTerritoryComponent
      legendMode={legendMode}
      setting={setting}
    />
  );
};
